import { Brain, Clock, Eye, Play, Video } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { nativeVideoUnderstanding } from 'sprites-types/workflow/tools/nativeVideoUnderstanding'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeVideoUnderstandingDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeVideoUnderstanding, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const videoUrl = inputs.videoUrl || inputs.url || inputs.video || ''
  const analysis =
    result && !Array.isArray(result) && 'analysis' in result
      ? String(result.analysis)
      : result && !Array.isArray(result) && 'description' in result
        ? String(result.description)
        : result && !Array.isArray(result) && 'understanding' in result
          ? String(result.understanding)
          : ''
  const summary =
    result && !Array.isArray(result) && 'summary' in result
      ? String(result.summary)
      : result && !Array.isArray(result) && 'overview' in result
        ? String(result.overview)
        : ''
  const keyPoints =
    result && !Array.isArray(result) && 'keyPoints' in result && Array.isArray(result.keyPoints)
      ? result.keyPoints
      : result &&
          !Array.isArray(result) &&
          'highlights' in result &&
          Array.isArray(result.highlights)
        ? result.highlights
        : []
  const duration =
    result && !Array.isArray(result) && 'duration' in result
      ? String(result.duration)
      : inputs.duration || ''
  const frameCount =
    result && !Array.isArray(result) && 'frameCount' in result
      ? Number(result.frameCount) || 0
      : result && !Array.isArray(result) && 'frames' in result
        ? Number(result.frames) || 0
        : 0
  const confidence =
    result && !Array.isArray(result) && 'confidence' in result
      ? Number(result.confidence) || 0
      : result && !Array.isArray(result) && 'score' in result
        ? Number(result.score) || 0
        : 0

  return (
    <ContextMenuWrapper actions={[]}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="px-4 py-3">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-purple-100 p-2">
                <Brain className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <Video className="h-4 w-4 text-purple-600" />
                <div className="text-sm font-semibold text-gray-900">
                  Video Understanding Complete
                </div>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-4 text-xs text-gray-600">
                {duration && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{duration}</span>
                  </div>
                )}
                {frameCount > 0 && (
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    <span>{frameCount} frames analyzed</span>
                  </div>
                )}
                {confidence > 0 && (
                  <div>
                    <span>Confidence: {Math.round(confidence * 100)}%</span>
                  </div>
                )}
              </div>

              {summary && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Summary:</div>
                  <div className="rounded-2xl bg-purple-50 p-2 text-xs text-gray-700">
                    {summary}
                  </div>
                </div>
              )}

              {keyPoints.length > 0 && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Key Points:</div>
                  <div className="space-y-1">
                    {keyPoints.slice(0, 5).map((point: string, index: number) => (
                      <div key={index} className="flex items-start gap-1 text-xs text-gray-600">
                        <div className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-purple-500" />
                        <span>{point}</span>
                      </div>
                    ))}
                    {keyPoints.length > 5 && (
                      <div className="text-xs text-gray-500">
                        +{keyPoints.length - 5} more insights
                      </div>
                    )}
                  </div>
                </div>
              )}

              {analysis && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Analysis:</div>
                  <div className="max-h-40 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-3">
                    <div className="whitespace-pre-wrap text-xs text-gray-800">
                      {typeof analysis === 'string'
                        ? analysis.length > 400
                          ? `${analysis.slice(0, 400)}...`
                          : analysis
                        : JSON.stringify(analysis, null, 2)}
                    </div>
                  </div>
                </div>
              )}

              {videoUrl && (
                <div className="flex items-center gap-3">
                  <a
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                  >
                    <Play className="h-3 w-3" />
                    View Original Video
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadNativeVideoUnderstanding(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function nativeVideoUnderstandingToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(nativeVideoUnderstanding, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const videoUrl = inputs.videoUrl || inputs.url || inputs.video || ''
  const analysis =
    result && !Array.isArray(result) && 'analysis' in result
      ? String(result.analysis)
      : result && !Array.isArray(result) && 'description' in result
        ? String(result.description)
        : result && !Array.isArray(result) && 'understanding' in result
          ? String(result.understanding)
          : ''
  const summary =
    result && !Array.isArray(result) && 'summary' in result
      ? String(result.summary)
      : result && !Array.isArray(result) && 'overview' in result
        ? String(result.overview)
        : ''
  const keyPoints =
    result && !Array.isArray(result) && 'keyPoints' in result && Array.isArray(result.keyPoints)
      ? result.keyPoints
      : result &&
          !Array.isArray(result) &&
          'highlights' in result &&
          Array.isArray(result.highlights)
        ? result.highlights
        : []

  return `## Video Understanding Analysis

${videoUrl ? `**Video URL:** ${videoUrl}\n\n` : ''}
${summary ? `**Summary:** ${summary}\n\n` : ''}
${keyPoints.length > 0 ? `**Key Points:**\n${keyPoints.map((point: string) => `- ${point}`).join('\n')}\n\n` : ''}
**Detailed Analysis:**
${typeof analysis === 'string' ? analysis : JSON.stringify(analysis, null, 2)}

---
`
}

export const NativeVideoUnderstandingDisplay = createWorkflowToolComponent(
  NativeVideoUnderstandingDisplayComponent,
  downloadNativeVideoUnderstanding,
  nativeVideoUnderstandingToMarkdown
)
